/**
 * Vincent B904 (Eric 2026-02-14 SPEC STRICK PROD) — Barre de recherche
 * globale par TAGS. Autocomplete sur les tags existants de l'org.
 * Mode ET (défaut : intersection) ou OU (union). Naviguer directement
 * vers la vidéo / texte en cliquant un résultat.
 *
 * Utilisé à 2 endroits (choix Vincent) :
 *   • Sidebar gauche (visible partout)
 *   • En haut de l'Explorateur de fichiers
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getLayoutBootstrap } from "../lib/layoutBootstrap"; // B1099
import { toast } from "sonner";
import { useLang } from "../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TagSearchBar({
  compact = false,
  allTags: allTagsProp = null,
  placeholder = null,
}) {
  const { tr } = useLang();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState("and"); // "and" par défaut (choix Vincent)
  const [allTags, setAllTags] = useState(allTagsProp || []);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);

  // Charge la liste des tags dispo si le parent n'en fournit pas.
  useEffect(() => {
    if (allTagsProp && allTagsProp.length) {
      setAllTags(allTagsProp);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // B1099 (Eric 2026-09-07) — Les modules/tags viennent de l'appel
        // agrégé du gabarit : cette barre est montée sur toutes les pages.
        const boot = await getLayoutBootstrap();
        if (boot?.modules) {
          if (!cancelled) setAllTags(boot.modules.tags || []);
          return;
        }
        const { data } = await axios.get(`${API}/api/videos/modules/list`, { withCredentials: true });
        if (!cancelled) setAllTags(data?.tags || []);
      } catch { /* silencieux — autocomplete non-critique */ }
    })();
    return () => { cancelled = true; };
  }, [allTagsProp]);

  // Fermeture dropdown au clic dehors.
  useEffect(() => {
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const suggestions = useMemo(() => {
    const q = input.trim().toLowerCase();
    const selectedNorm = new Set(selectedTags.map((t) => t.toLowerCase()));
    return (allTags || [])
      .filter((t) => t && !selectedNorm.has(t.toLowerCase()))
      .filter((t) => !q || t.toLowerCase().includes(q))
      .slice(0, 10);
  }, [input, allTags, selectedTags]);

  const runSearch = useCallback(async (tagsList, curMode) => {
    if (!tagsList || tagsList.length === 0) {
      setResults(null);
      setOpen(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/search/by-tags`, {
        params: { tags: tagsList.join(","), mode: curMode, limit: 100 },
        withCredentials: true,
      });
      setResults(data);
      setOpen(true);
      // Vincent B907 (Eric 2026-02-14 SPEC STRICK PROD) — Log fire-and-forget
      // pour alimenter la page Tag Analytics (top tags recherchés).
      axios.post(
        `${API}/api/search/tag-events`,
        { tags: tagsList, mode: curMode, results_count: data?.total || 0 },
        { withCredentials: true },
      ).catch(() => { /* silencieux : analytics ne doit rien bloquer */ });
    } catch (err) {
      toast.error(tr("Erreur : ", "Error: ") + (err?.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  const addTag = (t) => {
    const name = (t || "").trim();
    if (!name) return;
    if (selectedTags.some((s) => s.toLowerCase() === name.toLowerCase())) return;
    const next = [...selectedTags, name];
    setSelectedTags(next);
    setInput("");
    runSearch(next, mode);
  };
  const removeTag = (t) => {
    const next = selectedTags.filter((s) => s !== t);
    setSelectedTags(next);
    runSearch(next, mode);
  };
  const clearAll = () => {
    setSelectedTags([]);
    setResults(null);
    setInput("");
    setOpen(false);
  };
  const switchMode = (newMode) => {
    setMode(newMode);
    if (selectedTags.length > 0) runSearch(selectedTags, newMode);
  };
  const onKeyDown = (e) => {
    if (e.key === "Enter" && input.trim()) {
      e.preventDefault();
      addTag(input.trim());
    } else if (e.key === "Enter" && !input.trim() && selectedTags.length > 0) {
      // Vincent B906 (Eric 2026-02-14 SPEC STRICK PROD) — Entrée avec
      // input vide + ≥1 tag actif → navigate vers la page complète
      // de résultats (grille, dizaines de matches côte à côte).
      e.preventDefault();
      const params = new URLSearchParams();
      params.set("tags", selectedTags.join(","));
      params.set("mode", mode);
      setOpen(false);
      navigate({ pathname: "/dashboard/search", search: params.toString() });
    } else if (e.key === "Backspace" && !input && selectedTags.length > 0) {
      removeTag(selectedTags[selectedTags.length - 1]);
    }
  };

  const handleItemClick = (item) => {
    setOpen(false);
    navigate(`/dashboard/video/${item.id}`);
  };

  return (
    <div
      ref={containerRef}
      className={`relative ${compact ? "" : "mb-3"}`}
      data-testid="tag-search-bar"
      data-mode={mode}
    >
      <div
        className={`flex items-center flex-wrap gap-1.5 rounded border transition-colors ${
          focused ? "border-[#002FA7] ring-2 ring-[#002FA7]/20" : "border-zinc-300"
        } bg-white ${compact ? "px-2 py-1" : "px-2 py-1.5"}`}
      >
        <span className="text-zinc-400 text-xs" aria-hidden="true">🔎</span>
        {selectedTags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 text-[11px] bg-blue-50 text-blue-800 border border-blue-200 px-1.5 py-0.5 rounded-full"
            data-testid={`tag-search-active-${t}`}
          >
            {t}
            <button
              type="button"
              onClick={() => removeTag(t)}
              className="text-blue-600 hover:text-blue-900 font-bold"
              aria-label={`Retirer ${t}`}
              data-testid={`tag-search-remove-${t}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => { setInput(e.target.value); setOpen(true); }}
          onFocus={() => { setFocused(true); setOpen(true); }}
          onKeyDown={onKeyDown}
          placeholder={placeholder || (compact
            ? tr("Recherche par tag…", "Search by tag…")
            : tr("Rechercher par tag (Entrée pour ajouter)…", "Search by tag (Enter to add)…"))}
          className="flex-1 min-w-[120px] outline-none bg-transparent text-xs py-0.5"
          data-testid="tag-search-input"
        />
        {selectedTags.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => switchMode(mode === "and" ? "or" : "and")}
              className="text-[10px] font-semibold px-2 py-0.5 rounded bg-zinc-100 border border-zinc-300 text-zinc-700 hover:bg-zinc-200"
              title={mode === "and"
                ? "Mode ET (tous les tags requis). Cliquer pour passer en OU (au moins un)."
                : "Mode OU (au moins un tag). Cliquer pour passer en ET (tous requis)."}
              data-testid="tag-search-mode-toggle"
            >
              {mode === "and" ? "ET" : "OU"}
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="text-[10px] text-zinc-500 hover:text-zinc-800"
              data-testid="tag-search-clear-btn"
              title={tr("Effacer la recherche", "Clear the search")}
            >
              ✕
            </button>
          </>
        )}
        {loading && <span className="text-[10px] text-zinc-500">⏳</span>}
      </div>

      {open && (suggestions.length > 0 || results) && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 rounded-md border border-zinc-200 bg-white shadow-lg max-h-[70vh] overflow-y-auto"
          data-testid="tag-search-dropdown"
        >
          {suggestions.length > 0 && input && (
            <div className="border-b border-zinc-100 py-1">
              <p className="px-3 py-1 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Suggestions</p>
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => addTag(s)}
                  className="w-full text-left px-3 py-1 text-xs hover:bg-blue-50"
                  data-testid={`tag-search-suggestion-${s}`}
                >
                  <span className="text-blue-800">+ {s}</span>
                </button>
              ))}
            </div>
          )}
          {results && (
            <div className="py-1">
              <p className="px-3 py-1 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold flex items-center justify-between">
                <span data-testid="tag-search-results-count">
                  {results.total || 0} résultat{results.total > 1 ? "s" : ""}
                </span>
                <span className="text-zinc-400 normal-case">
                  {mode === "and" ? "ET" : "OU"} · {selectedTags.length} tag(s)
                </span>
              </p>
              {(results.items || []).length === 0 ? (
                <p className="px-3 py-2 text-xs text-zinc-500 italic">{tr("Aucun document ne correspond à ces tags.", "No document matches these tags.")}</p>
              ) : (
                (results.items || []).map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleItemClick(item)}
                    className="w-full text-left px-3 py-2 hover:bg-zinc-50 border-b border-zinc-50 last:border-b-0"
                    data-testid={`tag-search-result-${item.id}`}
                    data-kind={item.kind}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-sm" aria-hidden="true">
                        {item.kind === "text" ? "📄" : "🎬"}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-zinc-900 truncate">
                          {item.title || "(sans titre)"}
                        </p>
                        {item.description && (
                          <p className="text-[10px] text-zinc-500 truncate">{item.description}</p>
                        )}
                        {(item.tags || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {(item.tags || []).slice(0, 5).map((t) => (
                              <span
                                key={t}
                                className={`text-[9px] px-1 py-0 rounded ${
                                  selectedTags.some((s) => s.toLowerCase() === t.toLowerCase())
                                    ? "bg-blue-100 text-blue-800 font-semibold"
                                    : "bg-zinc-100 text-zinc-600"
                                }`}
                              >
                                {t}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
